import { ref, computed } from "vue";
import { boardedStorage } from "./useBoardedStorage";
import { buildSteps, isChecklistVisible, type OnboardingStep } from "../onboarding/onboarding-logic";
import useUserData from "./useUserData";
import useClaims from "./useClaims";

const TWENTY_ONE_DAYS_MS = 21 * 864e5;

export default function useOnboarding() {
  const { getUserBankAccounts, authUser } = useUserData();
  const { getCurrentUserClaims, claims } = useClaims();

  const storage = boardedStorage;

  const hasBankAccount = ref(false);
  const hasClaim = ref(false);

  const steps = computed<OnboardingStep[]>(() =>
    buildSteps({
      hasProfile: !!(authUser.value?.address && authUser.value?.contact),
      hasLocalOffice: !!authUser.value?.local_office,
      hasBankAccount: hasBankAccount.value,
      hasClaim: hasClaim.value,
    })
  );

  const total = 4;
  const completedCount = computed(() => steps.value.filter((s) => s.done).length);

  // 21-day reappearance: clear a stale dismissal at read time so the checklist can return.
  const effectiveDismissedAt = computed<string | null>(() => {
    const at = storage.value.checklistDismissedAt;
    if (at && Date.now() - new Date(at).getTime() > TWENTY_ONE_DAYS_MS) return null;
    return at;
  });

  const visible = computed(() =>
    isChecklistVisible({ steps: steps.value, dismissedAt: effectiveDismissedAt.value, nowMs: Date.now() })
  );

  const dismiss = () => {
    storage.value.checklistDismissedAt = new Date().toISOString();
  };

  const refresh = async () => {
    try {
      const accounts = await getUserBankAccounts();
      hasBankAccount.value = Array.isArray(accounts) ? accounts.length > 0 : !!accounts;
    } catch { hasBankAccount.value = false; }
    try {
      // getCurrentUserClaims populates the `claims` ref with { applications, total_results }.
      await getCurrentUserClaims({ page_size: 1, page_number: 1 });
      const payload = claims.value;
      hasClaim.value = Array.isArray(payload?.applications)
        ? payload.applications.length > 0
        : (payload?.total_results ?? 0) > 0;
    } catch { hasClaim.value = false; }
  };

  return { steps, visible, completedCount, total, dismiss, refresh };
}
